import { InternalAccessor } from '@billy/design-system'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { TransactionOriginatorType } from '@features/transactions/enums/transactionOriginatorType'
import { useBuildTransactionUrl } from '@features/transactions/hooks/useBuildTransactionUrl'
import { Transaction } from '@features/transactions/types/transaction'

import { TransactionsTableAccessor } from '../enums/TransactionsTableAccessor'
import { TransactionsTableRow } from '../types/transactionsTableRow'
import { getTransactionSideAmount } from '../utils/getTransactionSideAmount'
import { getTransactionTypeFromOriginatorReference } from '../utils/getTransactionTypeFromOriginatorReference'

export const useTransactionsTableData = (transactions: Transaction[]): TransactionsTableRow[] => {
  const { t } = useTranslation()
  const { getUrl } = useBuildTransactionUrl()

  return useMemo(
    () =>
      transactions.map(
        ({
          amount,
          createdTime,
          creator,
          entryDate,
          hasVoucher,
          id,
          journalCode,
          journalName,
          originatorCurrencyId,
          originatorName,
          originatorReference,
          transactionNo,
          isVoid,
          isVoided,
          side,
          voucherNo,
        }) => {
          const transactionType = getTransactionTypeFromOriginatorReference(amount, originatorReference, side, t)
          const originatorType = transactionType?.originatorType
          const translationKey = transactionType?.translationKey
          const originUrl = getUrl({ originatorId: transactionType?.originatorId, originatorType })

          const isBill = transactionType?.originatorType === TransactionOriginatorType.Bill
          const isInvoice = transactionType?.originatorType === TransactionOriginatorType.Invoice
          const isInvoiceLateFee = transactionType?.originatorType === TransactionOriginatorType.InvoiceLateFee
          const isContactBalance = transactionType?.originatorType === TransactionOriginatorType.ContactBalancePayment
          const isConversionBalance = transactionType?.originatorType === TransactionOriginatorType.ConversionBalance
          const isExternalInvoice = transactionType?.originatorType === TransactionOriginatorType.ExternalInvoice

          const isConsideredVoided = isVoid || isVoided
          const shouldShowHoverActions = (isBill || isInvoice) && !isConsideredVoided

          const shouldShowStaticActions =
            !isInvoiceLateFee && !isContactBalance && !isConversionBalance && !isExternalInvoice && !isConsideredVoided

          return {
            [TransactionsTableAccessor.Amount]: getTransactionSideAmount(amount, side),
            [TransactionsTableAccessor.CreatedBy]: creator?.name,
            [TransactionsTableAccessor.CreatedDate]: createdTime,
            [TransactionsTableAccessor.Currency]: originatorCurrencyId,
            [TransactionsTableAccessor.EntryDate]: entryDate,
            [TransactionsTableAccessor.Description]: originatorName,
            [TransactionsTableAccessor.Attachment]: hasVoucher,
            [TransactionsTableAccessor.JournalCode]: journalCode,
            [TransactionsTableAccessor.JournalName]: journalName,
            [TransactionsTableAccessor.Origin]: originUrl,
            [TransactionsTableAccessor.OriginatorTypeKey]: translationKey,
            [TransactionsTableAccessor.TransactionNo]: transactionNo,
            [TransactionsTableAccessor.Voided]: isConsideredVoided,
            [TransactionsTableAccessor.VoucherNo]: voucherNo,
            [InternalAccessor.HoverActions]: shouldShowHoverActions,
            [InternalAccessor.StaticActions]: shouldShowStaticActions,
            originatorId: transactionType?.originatorId || '',
            originatorType,
            id,
          }
        },
      ),
    [transactions, t, getUrl],
  )
}
